import { Form, useFormContext } from '@calendly/form';
import { useOnValueChange } from '@calendly/hooks';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo, forwardRef } from 'react';
import { useGetUserQuery } from 'common';
import { AutoSave } from 'components/core/form/auto-save';
import { useGetAvailabilitySchedulesQuery, useUpdateAvailabilityScheduleMutation } from '../api';
import { CalendarView } from './calendar_view';
import { DateSpecificHours } from './date_specific_hours';
import { PopupFooter } from './popup_footer';
import { encodeAvailabilityRule, getDefaultDays } from './utils';
import { validationSchema } from './validation';
import { WeeklyHoursSection } from './weekly_hours';
const containerIsTabletUpCss = "ccb71ky";
const containerWithinModalCss = "cazu0l6";
const containerCss = "ci239km";
const Container = forwardRef((props, ref) => {
  const {
    withinModal,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTabletUp = useDeviceType('isTabletUp');
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsTabletUpCss]: isTabletUp,
    [containerWithinModalCss]: withinModal
  }, props.className)} />;
});
const calendarContainerWithinModalCss = "c1n4y6iw";
const calendarContainerCss = "c3ns3bm";
const CalendarContainer = forwardRef((props, ref) => {
  const {
    withinModal,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(calendarContainerCss, {
    [calendarContainerWithinModalCss]: withinModal
  }, props.className)} />;
});
const DirtyTracking = ({
  onChange
}) => {
  const {
    dirty
  } = useFormContext();
  useOnValueChange(dirty, () => {
    onChange(dirty);
  });
};
const getSpecificDates = rules => rules.filter(({
  type
}) => type === 'date');
export const Schedules = ({
  formInnerRef,
  formContentRef,
  setIsFormDirty,
  selectedScheduleId,
  currentEventType,
  trackLocation,
  onClose,
  withinModal: withinModalProp
}) => {
  const layoutWithinModal = withinModalProp ?? Boolean(onClose);
  const {
    data: {
      availability_form_view: formView
    }
  } = useGetUserQuery();
  const {
    data: allSchedules = []
  } = useGetAvailabilitySchedulesQuery();
  const isTabletUp = useDeviceType('isTabletUp');
  const isListView = formView === 'list';
  const isCalendarView = formView === 'calendar';
  const [updateAvailabilitySchedule] = useUpdateAvailabilityScheduleMutation({
    fixedCacheKey: `update-availability-schedule-${selectedScheduleId}`
  });
  const selectedSchedule = allSchedules.find(({
    id: scheduleId
  }) => scheduleId === selectedScheduleId);
  const initialValues = useMemo(() => ({
    days: getDefaultDays(selectedSchedule.rules),
    timezone: selectedSchedule.timezone,
    activeDates: getSpecificDates(selectedSchedule.rules)
  }), [selectedSchedule]);
  const handleSubmit = useCallback(values => {
    if (isEqual(values, initialValues)) {
      if (onClose) {
        onClose();
      }
      return Promise.resolve();
    }
    return updateAvailabilitySchedule({
      updatedSchedule: encodeAvailabilityRule(selectedSchedule, values),
      onSuccess: data => {
        if (onClose) {
          onClose({
            updatedSchedule: data
          });
        }
      }
    });
  }, [initialValues, onClose, selectedSchedule, updateAvailabilitySchedule]);
  return <Form innerRef={formInnerRef} initialValues={initialValues} validationSchema={validationSchema} validateOnChange onSubmit={handleSubmit} enableReinitialize>
      {(isListView || !isTabletUp) && <Container withinModal={layoutWithinModal}>
          <WeeklyHoursSection />
          <DateSpecificHours trackLocation={trackLocation} />
        </Container>}
      {isCalendarView && isTabletUp && <CalendarContainer withinModal={layoutWithinModal}>
          <CalendarView />
        </CalendarContainer>}
      {onClose ? <PopupFooter selectedScheduleId={selectedScheduleId} currentEventType={currentEventType} onClose={onClose} /> : <>
          <AutoSave formContentRef={formContentRef} />
          <DirtyTracking onChange={setIsFormDirty} />
        </>}
    </Form>;
};

require("./schedules.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./schedules.js");